'use client';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Headphones,
  Sparkles,
  Loader2,
  AlertCircle,
  RefreshCw,
  Download,
  Lock,
  CheckCircle2,
  Crown,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import AudioPlayer from './AudioPlayer';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AudioAssetResult {
  id: string;
  scriptId: string;
  assetType: 'MORNING_MEDITATION' | 'EVENING_MEDITATION';
  status: 'COMPLETED' | 'FAILED' | 'PROCESSING';
  publicUrl: string | null;
  storagePath: string | null;
  backgroundTrackKey: string | null;
  durationSeconds: number | null;
  errorMessage: string | null;
  canDownload: boolean;
}

interface EntitlementState {
  allowed: boolean;
  reason: string;
  remainingSlots: number;
  hasAudioAccess: boolean;
  oneOffAudioPack: boolean;
  audioGenerationsUsed: number;
  audioGenerationsLimit: number;
}

type GenerationState =
  | 'idle' |'checking' |'generating' |'completed' |'failed' |'not_paid' |'limit_reached';

interface AudioGenerationPanelProps {
  userId: string;
  morningTitle: string;
  eveningTitle: string;
  morningDuration: string;
  eveningDuration: string;
}

// ─── Polling interval ─────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 4000;
const MAX_POLL_ATTEMPTS = 30; // 2 minutes max

// ─── Component ────────────────────────────────────────────────────────────────

export default function AudioGenerationPanel({
  userId,
  morningTitle,
  eveningTitle,
  morningDuration,
  eveningDuration,
}: AudioGenerationPanelProps) {
  const [generationState, setGenerationState] = useState<GenerationState>('idle');
  const [morningAudio, setMorningAudio] = useState<AudioAssetResult | null>(null);
  const [eveningAudio, setEveningAudio] = useState<AudioAssetResult | null>(null);
  const [entitlement, setEntitlement] = useState<EntitlementState | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [showDevPanel, setShowDevPanel] = useState(false);
  const [devToggling, setDevToggling] = useState(false);
  const [devMessage, setDevMessage] = useState<string | null>(null);

  // ── Load existing audio status on mount ─────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    loadStatus();
  }, [userId]);

  const loadStatus = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`/api/audio/status?userId=${encodeURIComponent(userId)}`);
      if (!res.ok) return;
      const data = await res.json();
      setEntitlement(data.entitlement ?? null);

      if (data.morningAudio || data.eveningAudio) {
        setMorningAudio(data.morningAudio);
        setEveningAudio(data.eveningAudio);

        const hasProcessing =
          data.morningAudio?.status === 'PROCESSING' ||
          data.eveningAudio?.status === 'PROCESSING';

        const hasCompleted =
          data.morningAudio?.status === 'COMPLETED' ||
          data.eveningAudio?.status === 'COMPLETED';

        const allFailed =
          (!data.morningAudio || data.morningAudio.status === 'FAILED') &&
          (!data.eveningAudio || data.eveningAudio.status === 'FAILED');

        if (hasProcessing) {
          setGenerationState('generating');
        } else if (hasCompleted) {
          setGenerationState('completed');
        } else if (allFailed) {
          // Both failed (including stale PROCESSING treated as FAILED) — show retry CTA
          setGenerationState('failed');
          setErrorMessage('Audio generation failed. Please try again.');
        }
      }
    } catch {
      // Silently fail — don't break the page
    }
  }, [userId]);

  // ── Polling for PROCESSING state ─────────────────────────────────────────
  useEffect(() => {
    if (generationState !== 'generating') return;
    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
      setGenerationState('failed');
      setErrorMessage('Audio generation is taking longer than expected. Please try again.');
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/audio/status?userId=${encodeURIComponent(userId)}`);
        if (!res.ok) return;
        const data = await res.json();

        setMorningAudio(data.morningAudio);
        setEveningAudio(data.eveningAudio);
        setEntitlement(data.entitlement ?? null);

        const stillProcessing =
          data.morningAudio?.status === 'PROCESSING' ||
          data.eveningAudio?.status === 'PROCESSING';

        const anyCompleted =
          data.morningAudio?.status === 'COMPLETED' ||
          data.eveningAudio?.status === 'COMPLETED';

        const allFailed =
          (!data.morningAudio || data.morningAudio.status === 'FAILED') &&
          (!data.eveningAudio || data.eveningAudio.status === 'FAILED');

        if (stillProcessing) {
          setPollAttempts((n) => n + 1);
        } else if (anyCompleted) {
          setGenerationState('completed');
          setPollAttempts(0);
        } else if (allFailed) {
          setGenerationState('failed');
          setErrorMessage('We couldn\'t generate your audio this time. Please try again in a few minutes.');
          setPollAttempts(0);
        }
      } catch {
        setPollAttempts((n) => n + 1);
      }
    }, POLL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [generationState, pollAttempts, userId]);

  // ── Generate handler ──────────────────────────────────────────────────────
  async function handleGenerate() {
    if (!userId) return;
    setGenerationState('generating');
    setErrorMessage(null);
    setPollAttempts(0);

    try {
      const res = await fetch('/api/audio/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });

      const data = await res.json();

      if (res.status === 402) {
        if (data.errorCode === 'not_paid') {
          setGenerationState('not_paid');
          setEntitlement(data.entitlementState ? {
            allowed: false,
            reason: 'not_paid',
            remainingSlots: 0,
            hasAudioAccess: false,
            oneOffAudioPack: false,
            audioGenerationsUsed: data.entitlementState.audioGenerationsUsed ?? 0,
            audioGenerationsLimit: data.entitlementState.audioGenerationsLimit ?? 0,
          } : null);
        } else {
          setGenerationState('limit_reached');
        }
        return;
      }

      if (!res.ok || !data.success) {
        setGenerationState('failed');
        setErrorMessage(data.error ?? 'Audio generation failed. Please try again.');
        return;
      }

      // Generation succeeded or is in progress
      setMorningAudio(data.morningAudio);
      setEveningAudio(data.eveningAudio);

      const hasProcessing =
        data.morningAudio?.status === 'PROCESSING' ||
        data.eveningAudio?.status === 'PROCESSING';

      const hasCompleted =
        data.morningAudio?.status === 'COMPLETED' ||
        data.eveningAudio?.status === 'COMPLETED';

      if (hasCompleted && !hasProcessing) {
        setGenerationState('completed');
      } else {
        setGenerationState('generating'); // triggers polling
      }
    } catch {
      setGenerationState('failed');
      setErrorMessage('We couldn\'t generate your audio this time. Please try again in a few minutes.');
    }
  }

  // ── Dev toggle ────────────────────────────────────────────────────────────
  async function handleDevToggle() {
    if (!userId) return;
    setDevToggling(true);
    setDevMessage(null);
    try {
      const res = await fetch('/api/audio/toggle-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const data = await res.json();
      setDevMessage(data.message ?? (data.success ? 'Done' : 'Failed'));
      await loadStatus();
    } catch {
      setDevMessage('Toggle failed');
    } finally {
      setDevToggling(false);
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  function formatDuration(seconds: number | null): string {
    if (!seconds) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m} min`;
  }

  const hasAnyAudio = morningAudio || eveningAudio;
  const hasCompletedAudio =
    morningAudio?.status === 'COMPLETED' || eveningAudio?.status === 'COMPLETED';

  // Show the generate CTA whenever entitlement is allowed and we're idle
  // (regardless of whether stale/failed audio records exist)
  const allAudioFailed =
    hasAnyAudio &&
    (!morningAudio || morningAudio.status === 'FAILED') &&
    (!eveningAudio || eveningAudio.status === 'FAILED');

  const showGenerateCTA =
    generationState === 'idle' &&
    entitlement?.allowed === true &&
    (!hasAnyAudio || allAudioFailed);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* ── Audio section header ──────────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 rounded-xl bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0">
          <Headphones size={16} className="text-[#2A9D8F]" />
        </div>
        <div>
          <p className="font-bold text-[#1C1917] text-base">Your personalised audio</p>
          <p className="text-xs text-[#9B9590] mt-0.5">
            {hasCompletedAudio
              ? 'Your meditations are ready to play' :'Generate audio versions of your morning and evening practices'}
          </p>
        </div>
      </div>

      {/* ── NOT PAID state ────────────────────────────────────────────────── */}
      {(generationState === 'not_paid' || (!hasAnyAudio && entitlement && !entitlement.allowed && entitlement.reason === 'not_paid')) && (
        <div className="rounded-2xl p-5 border border-amber-200 bg-amber-50/60">
          <div className="flex items-start gap-3">
            <Lock size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-[#1C1917] mb-1">Audio is a paid feature</p>
              <p className="text-sm text-[#6B6560] leading-relaxed mb-4">
                Upgrade to access personalised audio meditations — your morning and evening practices,
                voiced and mixed with calming background sound, built around your goals and patterns.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/pricing-upgrade"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-[#2A9D8F] rounded-xl px-4 py-2.5 hover:bg-[#238A7A] transition-colors"
                >
                  <Crown size={14} />
                  Upgrade to unlock audio
                </a>
                <button
                  onClick={() => setShowDevPanel((v) => !v)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#9B9590] hover:text-[#6B6560] transition-colors"
                >
                  {showDevPanel ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  Dev options
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── LIMIT REACHED state ───────────────────────────────────────────── */}
      {generationState === 'limit_reached' && (
        <div className="rounded-2xl p-5 border border-amber-200 bg-amber-50/60">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-bold text-[#1C1917] mb-1">Generation limit reached</p>
              <p className="text-sm text-[#6B6560] leading-relaxed">
                You&apos;ve used all your audio generation slots. Upgrade your plan to generate more.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── IDLE / GENERATE CTA ───────────────────────────────────────────── */}
      {showGenerateCTA && (
        <div className="rounded-2xl p-5 border border-[#2A9D8F]/20 bg-gradient-to-br from-[#2A9D8F]/05 to-transparent">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#2A9D8F]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles size={18} className="text-[#2A9D8F]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-[#1C1917] mb-1">Generate your morning &amp; evening audio</p>
              <p className="text-sm text-[#6B6560] leading-relaxed mb-4">
                Your meditation scripts will be voiced and mixed with calming background sound —
                personalised for your goals and patterns.
              </p>
              {entitlement.audioGenerationsLimit > 0 && (
                <p className="text-xs text-[#9B9590] mb-3">
                  {entitlement.audioGenerationsLimit - entitlement.audioGenerationsUsed} generation{entitlement.audioGenerationsLimit - entitlement.audioGenerationsUsed !== 1 ? 's' : ''} remaining
                </p>
              )}
              <button
                onClick={handleGenerate}
                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#2A9D8F] rounded-xl px-5 py-2.5 hover:bg-[#238A7A] active:scale-95 transition-all"
              >
                <Headphones size={15} />
                Generate your audio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── GENERATING / LOADING state ────────────────────────────────────── */}
      {generationState === 'generating' && (
        <div className="rounded-2xl p-5 border border-[#2A9D8F]/20 bg-[#2A9D8F]/04">
          <div className="flex items-center gap-3">
            <Loader2 size={20} className="text-[#2A9D8F] animate-spin flex-shrink-0" />
            <div>
              <p className="font-semibold text-[#1C1917] text-sm">Generating your personalised audio…</p>
              <p className="text-xs text-[#9B9590] mt-0.5">
                This takes 1–2 minutes. Your voice track is being created and mixed with background sound.
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {[
              { label: 'Morning meditation', asset: morningAudio },
              { label: 'Evening meditation', asset: eveningAudio },
            ].map(({ label, asset }) => (
              <div key={label} className="flex items-center gap-2">
                {asset?.status === 'COMPLETED' ? (
                  <CheckCircle2 size={14} className="text-[#52B788] flex-shrink-0" />
                ) : (
                  <Loader2 size={14} className="text-[#2A9D8F] animate-spin flex-shrink-0" />
                )}
                <span className="text-xs text-[#6B6560]">
                  {label} — {asset?.status === 'COMPLETED' ? 'ready' : 'generating…'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── FAILED state ──────────────────────────────────────────────────── */}
      {generationState === 'failed' && (
        <div className="rounded-2xl p-5 border border-red-200 bg-red-50/60">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-[#1C1917] mb-1">Audio generation failed</p>
              <p className="text-sm text-[#6B6560] leading-relaxed mb-3">
                {errorMessage ?? 'We couldn\'t generate your audio this time. Please try again in a few minutes.'}
              </p>
              <button
                onClick={handleGenerate}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#2A9D8F] border border-[#2A9D8F]/30 rounded-xl px-4 py-2 hover:bg-[#2A9D8F]/05 transition-colors"
              >
                <RefreshCw size={14} />
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── COMPLETED — Audio players ─────────────────────────────────────── */}
      {(generationState === 'completed' || hasCompletedAudio) && (
        <div className="space-y-4">
          {/* Morning audio */}
          {morningAudio && (
            <div className="space-y-2">
              <AudioPlayer
                title={morningTitle}
                duration={morningAudio.durationSeconds ? formatDuration(morningAudio.durationSeconds) : morningDuration}
                audioUrl={morningAudio.status === 'COMPLETED' ? morningAudio.publicUrl : null}
              />
              {morningAudio.status === 'COMPLETED' && morningAudio.canDownload && morningAudio.publicUrl && (
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs text-white/60">
                    {morningAudio.backgroundTrackKey
                      ? `Mixed with ${morningAudio.backgroundTrackKey.replace(/_/g, ' ')}`
                      : 'Personalised audio'}
                  </span>
                  <a
                    href={morningAudio.publicUrl}
                    download="morning-meditation.mp3"
                    className="inline-flex items-center gap-1 text-xs font-medium text-white/70 hover:text-white transition-colors"
                  >
                    <Download size={12} />
                    Download
                  </a>
                </div>
              )}
              {morningAudio.status === 'FAILED' && (
                <p className="text-xs text-red-400 px-1">Morning audio failed — try regenerating</p>
              )}
            </div>
          )}

          {/* Evening audio */}
          {eveningAudio && (
            <div className="space-y-2">
              <AudioPlayer
                title={eveningTitle}
                duration={eveningAudio.durationSeconds ? formatDuration(eveningAudio.durationSeconds) : eveningDuration}
                audioUrl={eveningAudio.status === 'COMPLETED' ? eveningAudio.publicUrl : null}
              />
              {eveningAudio.status === 'COMPLETED' && eveningAudio.canDownload && eveningAudio.publicUrl && (
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs text-white/60">
                    {eveningAudio.backgroundTrackKey
                      ? `Mixed with ${eveningAudio.backgroundTrackKey.replace(/_/g, ' ')}`
                      : 'Personalised audio'}
                  </span>
                  <a
                    href={eveningAudio.publicUrl}
                    download="evening-meditation.mp3"
                    className="inline-flex items-center gap-1 text-xs font-medium text-white/70 hover:text-white transition-colors"
                  >
                    <Download size={12} />
                    Download
                  </a>
                </div>
              )}
              {eveningAudio.status === 'FAILED' && (
                <p className="text-xs text-red-400 px-1">Evening audio failed — try regenerating</p>
              )}
            </div>
          )}

          {/* Regenerate option */}
          {hasCompletedAudio && generationState === 'completed' && (
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#9B9590] hover:text-[#6B6560] transition-colors mt-1"
            >
              <RefreshCw size={12} />
              Regenerate audio
            </button>
          )}
        </div>
      )}

      {/* ── No audio yet + not checked entitlement ────────────────────────── */}
      {generationState === 'idle' && !hasAnyAudio && !entitlement && (
        <div className="rounded-2xl p-4 border border-[#EAE6E0] bg-[#FAF8F5]">
          <p className="text-sm text-[#9B9590] text-center">Loading audio status…</p>
        </div>
      )}

      {/* ── DEV PANEL ─────────────────────────────────────────────────────── */}
      <DevPanel
        userId={userId}
        show={showDevPanel}
        onToggle={() => setShowDevPanel((v) => !v)}
        onDevToggle={handleDevToggle}
        devToggling={devToggling}
        devMessage={devMessage}
        entitlement={entitlement}
        onRefresh={loadStatus}
      />
    </div>
  );
}

// ─── Dev Panel ────────────────────────────────────────────────────────────────

function DevPanel({
  userId,
  show,
  onToggle,
  onDevToggle,
  devToggling,
  devMessage,
  entitlement,
  onRefresh,
}: {
  userId: string;
  show: boolean;
  onToggle: () => void;
  onDevToggle: () => void;
  devToggling: boolean;
  devMessage: string | null;
  entitlement: EntitlementState | null;
  onRefresh: () => void;
}) {
  if (!userId) return null;

  return (
    <div className="mt-2">
      <button
        onClick={onToggle}
        className="inline-flex items-center gap-1 text-xs font-medium text-[#C8C4BE] hover:text-[#9B9590] transition-colors"
      >
        {show ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        Developer options
      </button>

      {show && (
        <div className="mt-3 rounded-xl border border-dashed border-[#EAE6E0] p-4 bg-[#FAF8F5]">
          <p className="text-xs font-semibold text-[#9B9590] uppercase tracking-widest mb-3">
            Dev / Testing
          </p>

          <div className="space-y-2 mb-3">
            <div className="flex items-center justify-between text-xs text-[#6B6560]">
              <span>Audio access</span>
              <span className={entitlement?.hasAudioAccess ? 'text-[#52B788] font-semibold' : 'text-[#9B9590]'}>
                {entitlement?.hasAudioAccess ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-[#6B6560]">
              <span>Generations used / limit</span>
              <span>{entitlement?.audioGenerationsUsed ?? 0} / {entitlement?.audioGenerationsLimit ?? 0}</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={onDevToggle}
              disabled={devToggling}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#6B6560] border border-[#EAE6E0] rounded-lg px-3 py-1.5 hover:bg-white transition-colors disabled:opacity-50"
            >
              {devToggling ? <Loader2 size={11} className="animate-spin" /> : null}
              {entitlement?.hasAudioAccess ? 'Disable audio access' : 'Enable audio access (test)'}
            </button>
            <button
              onClick={onRefresh}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[#6B6560] border border-[#EAE6E0] rounded-lg px-3 py-1.5 hover:bg-white transition-colors"
            >
              <RefreshCw size={11} />
              Refresh status
            </button>
          </div>

          {devMessage && (
            <p className="text-xs text-[#52B788] mt-2">{devMessage}</p>
          )}
        </div>
      )}
    </div>
  );
}
